import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  FolderKanban,
  Lightbulb,
  Link2,
  Package,
  Pencil,
  Pin,
  Scale,
  Send,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { TagBadge } from "@/components/ui/TagBadge";
import { VoteButtons } from "@/components/ui/VoteButtons";
import { IdeasMultiSelect } from "../components/IdeasMultiSelect";
import { AssociationFields } from "@/components/shared/AssociationFields";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import { TasksTabContent } from "@/modules/tasks/components/TasksTabContent";
import { ActionsTabContent } from "@/modules/actions/components/ActionsTabContent";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useAddDecisionComment,
  useCreateDecision,
  useDecision,
  useDecisionCategories,
  useDecisionComments,
  useDecisionImpacts,
  useDecisionStatuses,
  useDecisionTags,
  useDeleteDecision,
  useDeleteDecisionComment,
  useLinkIdea,
  useRemoveDecisionVote,
  useToggleDecisionPin,
  useUnlinkIdea,
  useUpdateDecision,
  useVoteDecision,
} from "../hooks/useDecisions";

const BORDER = "hsl(210,30%,85%)";

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function countWords(html: string): number {
  const text = stripHtml(html);
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatRelative(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const STATUS_COLORS: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  open: {
    bg: "hsl(210,85%,95%)",
    text: "hsl(210,85%,38%)",
    border: "hsl(210,75%,82%)",
  },
  decided: {
    bg: "hsl(142,55%,94%)",
    text: "hsl(142,65%,30%)",
    border: "hsl(142,45%,78%)",
  },
  implemented: {
    bg: "hsl(180,60%,93%)",
    text: "hsl(180,70%,28%)",
    border: "hsl(180,50%,75%)",
  },
  archived: {
    bg: "hsl(215,15%,94%)",
    text: "hsl(215,20%,38%)",
    border: "hsl(215,12%,82%)",
  },
};

const IMPACT_COLORS: Record<string, { bg: string; text: string }> = {
  critical: { bg: "hsl(0,80%,95%)", text: "hsl(0,75%,38%)" },
  high: { bg: "hsl(25,95%,94%)", text: "hsl(25,85%,38%)" },
  medium: { bg: "hsl(40,95%,94%)", text: "hsl(35,85%,38%)" },
  low: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

function StatusBadge({ value, label }: { value: string; label: string }) {
  const c = STATUS_COLORS[value] ?? STATUS_COLORS.open;
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: c.bg, color: c.text, borderColor: c.border }}
    >
      {label}
    </span>
  );
}

function ImpactBadge({ value, label }: { value: string; label: string }) {
  const c = IMPACT_COLORS[value] ?? IMPACT_COLORS.medium;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: c.bg, color: c.text }}
    >
      {label}
    </span>
  );
}

function IconBtn({
  icon: Icon,
  label,
  onClick,
  active,
  color,
  disabled,
}: {
  icon: typeof Pin;
  label: string;
  onClick: () => void;
  active?: boolean;
  color?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground disabled:opacity-50"
      style={{ borderColor: BORDER }}
    >
      <Icon
        className="h-[15px] w-[15px]"
        strokeWidth={1.5}
        style={{
          color: active && color ? color : undefined,
          fill: active && color ? color : "none",
        }}
      />
    </button>
  );
}

type Heading = { id: string; level: number; text: string };

function extractHeadings(html: string): Heading[] {
  if (!html) return [];
  const div = document.createElement("div");
  div.innerHTML = html;
  const nodes = Array.from(div.querySelectorAll("h1,h2,h3"));
  return nodes.map((el, i) => ({
    id: `heading-${i}`,
    level: parseInt(el.tagName.substring(1), 10),
    text: el.textContent ?? "",
  }));
}

export default function DecisionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const decisionId = id ?? "";

  const { data: decision, isLoading, isError } = useDecision(decisionId);
  const { data: comments = [] } = useDecisionComments(decisionId);

  const updateDecision = useUpdateDecision(decisionId);
  const togglePin = useToggleDecisionPin();
  const deleteDecision = useDeleteDecision();
  const createDecision = useCreateDecision();
  const addComment = useAddDecisionComment(decisionId);
  const deleteComment = useDeleteDecisionComment(decisionId);
  const voteDecision = useVoteDecision(decisionId);
  const removeVote = useRemoveDecisionVote(decisionId);
  const linkIdea = useLinkIdea(decisionId);
  const unlinkIdea = useUnlinkIdea(decisionId);

  const statuses = useDecisionStatuses().data ?? [];
  const categories = useDecisionCategories().data ?? [];
  const impacts = useDecisionImpacts().data ?? [];
  const tagOptions = useDecisionTags().data ?? [];

  const [editingMeta, setEditingMeta] = useState(false);
  const [editingContent, setEditingContent] = useState(
    search.get("edit") === "true",
  );
  const [titleDraft, setTitleDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("open");
  const [categoryDraft, setCategoryDraft] = useState<string>("");
  const [impactDraft, setImpactDraft] = useState<string>("medium");
  const [visibilityDraft, setVisibilityDraft] = useState<"team" | "private">(
    "team",
  );
  const [tagsDraft, setTagsDraft] = useState<string[]>([]);
  const [decidedAtDraft, setDecidedAtDraft] = useState("");
  const [reviewDateDraft, setReviewDateDraft] = useState("");
  const [isPinnedDraft, setIsPinnedDraft] = useState(false);
  const [decidedByIdDraft, setDecidedByIdDraft] = useState<string | null>(null);
  const [companyIdDraft, setCompanyIdDraft] = useState<string | null>(null);
  const [productIdDraft, setProductIdDraft] = useState<string | null>(null);
  const [projectIdDraft, setProjectIdDraft] = useState<string | null>(null);
  const [meetingIdDraft, setMeetingIdDraft] = useState<string | null>(null);
  const [ideaIdsDraft, setIdeaIdsDraft] = useState<string[]>([]);
  const [contentDraft, setContentDraft] = useState("");

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];
  const [tab, setTab] = useState<
    "linkedIdeas" | "tasks" | "actions" | "comments" | "attachments"
  >("linkedIdeas");
  const [metaExpanded, setMetaExpanded] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [unlinkIdeaId, setUnlinkIdeaId] = useState<string | null>(null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialContent = useRef<string>("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
    "idle",
  );

  useEffect(() => {
    if (decision) {
      setTitleDraft(decision.title);
      setStatusDraft(decision.status);
      setCategoryDraft(decision.category ?? "");
      setImpactDraft(decision.impact);
      setVisibilityDraft(
        (decision.visibility as "team" | "private") ?? "team",
      );
      setTagsDraft(decision.tags ?? []);
      setDecidedAtDraft(
        decision.decidedAt ? decision.decidedAt.slice(0, 10) : "",
      );
      setReviewDateDraft(
        decision.reviewDate ? decision.reviewDate.slice(0, 10) : "",
      );
      setIsPinnedDraft(decision.isPinned);
      setDecidedByIdDraft(decision.decidedById ?? null);
      setCompanyIdDraft(decision.companyId ?? null);
      setProductIdDraft(decision.productId ?? null);
      setProjectIdDraft(decision.projectId ?? null);
      setMeetingIdDraft(decision.meetingId ?? null);
      setIdeaIdsDraft((decision.linkedIdeas ?? []).map((i) => i.id));
      setContentDraft(decision.content);
      initialContent.current = decision.content;
    }
  }, [decision?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const wordCount = useMemo(() => countWords(contentDraft), [contentDraft]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  useEffect(() => {
    if (!editingContent || !decision) return;
    if (contentDraft === initialContent.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("idle");
    saveTimer.current = setTimeout(() => {
      void doContentSave();
    }, 2000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [contentDraft, editingContent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key.toLowerCase() === "s" &&
        editingContent
      ) {
        e.preventDefault();
        void doContentSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingContent, contentDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  async function doContentSave() {
    if (!decision) return;
    setSaveState("saving");
    try {
      await updateDecision.mutateAsync({ content: contentDraft });
      initialContent.current = contentDraft;
      setSaveState("saved");
    } catch {
      setSaveState("idle");
    }
  }

  async function handleMetaSave() {
    if (!decision) return;
    try {
      await updateDecision.mutateAsync({
        title: titleDraft.trim() || decision.title,
        status: statusDraft,
        category: categoryDraft || null,
        impact: impactDraft,
        visibility: visibilityDraft,
        tags: tagsDraft,
        decidedAt: decidedAtDraft
          ? new Date(decidedAtDraft).toISOString()
          : null,
        reviewDate: reviewDateDraft
          ? new Date(reviewDateDraft).toISOString()
          : null,
        isPinned: isPinnedDraft,
        decidedById: decidedByIdDraft,
        companyId: companyIdDraft,
        productId: productIdDraft,
        projectId: projectIdDraft,
        meetingId: meetingIdDraft,
        ideaIds: ideaIdsDraft,
      });
      setEditingMeta(false);
      showToast("Decision updated");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to save");
    }
  }

  async function handleTogglePin() {
    if (!decision) return;
    await togglePin.mutateAsync({
      id: decision.id,
      isPinned: !decision.isPinned,
    });
    showToast(decision.isPinned ? "Unpinned" : "Pinned");
  }

  function handleDelete() {
    if (!decision) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!decision) return;
    await deleteDecision.mutateAsync(decision.id);
    navigate("/decisions");
  }

  async function handleDuplicate() {
    if (!decision) return;
    const res = await createDecision.mutateAsync({
      title: `${decision.title} (Copy)`,
      content: decision.content,
      status: "open",
      category: decision.category,
      impact: decision.impact,
      tags: decision.tags,
      visibility: decision.visibility,
      companyId: decision.companyId,
      productId: decision.productId,
      projectId: decision.projectId,
      phaseId: decision.phaseId,
      stageId: decision.stageId,
      meetingId: decision.meetingId,
    });
    showToast("Decision duplicated");
    navigate(`/decisions/${res.decision.id}`);
  }

  async function handleVote(type: "up" | "down") {
    if (!decision) return;
    if (decision.currentUserVote === type) {
      await removeVote.mutateAsync();
    } else {
      await voteDecision.mutateAsync(type);
    }
  }

  async function handleAddComment() {
    const text = commentText.trim();
    if (!text) return;
    setCommentText("");
    try {
      await addComment.mutateAsync(text);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to add comment");
    }
  }

  async function handleLinkIdea(ideaId: string) {
    try {
      await linkIdea.mutateAsync(ideaId);
      setLinkPickerOpen(false);
      showToast("Idea linked");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to link idea");
    }
  }

  function handleUnlinkIdea(ideaId: string) {
    setUnlinkIdeaId(ideaId);
  }

  async function confirmUnlinkIdea() {
    if (!unlinkIdeaId) return;
    await unlinkIdea.mutateAsync(unlinkIdeaId);
    showToast("Idea unlinked");
  }

  const { data: ideasResult } = useIdeaList({
    page: 1,
    limit: 50,
    search: linkSearch || undefined,
  });
  const ideasForLink = ideasResult?.data ?? [];

  const headings = useMemo(
    () => extractHeadings(editingContent ? contentDraft : decision?.content ?? ""),
    [decision?.content, contentDraft, editingContent],
  );

  if (isLoading) {
    return (
      <AppLayout breadcrumb={[{ label: "Decisions", href: "/decisions" }]}>
        <div className="py-16 text-center text-sm text-muted-foreground">
          Loading decision…
        </div>
      </AppLayout>
    );
  }
  if (isError || !decision) {
    return (
      <AppLayout breadcrumb={[{ label: "Decisions", href: "/decisions" }]}>
        <div className="py-16 text-center text-sm text-destructive">
          Decision not found.
        </div>
      </AppLayout>
    );
  }

  const statusLabel =
    statuses.find((s) => s.value === decision.status)?.label ?? decision.status;
  const categoryLabel = decision.category
    ? (categories.find((c) => c.value === decision.category)?.label ??
      decision.category)
    : null;
  const impactLabel =
    impacts.find((i) => i.value === decision.impact)?.label ?? decision.impact;

  const showTimeline =
    decision.status === "decided" || decision.status === "implemented";
  const linkedIdeas = decision.linkedIdeas ?? [];

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Planning" },
        { label: "Decisions", href: "/decisions" },
        { label: decision.code ?? decision.title },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/decisions")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
              style={{ borderColor: BORDER }}
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <Scale className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-xl font-semibold">View Decision</h1>
          </div>
          <div className="flex items-center gap-1.5">
            <IconBtn
              icon={Pin}
              label={decision.isPinned ? "Unpin" : "Pin"}
              onClick={handleTogglePin}
              active={decision.isPinned}
              color="hsl(40,95%,55%)"
            />
            <IconBtn icon={Copy} label="Duplicate" onClick={handleDuplicate} />
            <IconBtn
              icon={Clock}
              label="History"
              onClick={() => showToast("History coming soon")}
            />
            <IconBtn
              icon={Pencil}
              label="Edit"
              onClick={() => setEditingMeta((v) => !v)}
              active={editingMeta}
            />
            <IconBtn icon={Trash2} label="Delete" onClick={handleDelete} />
          </div>
        </div>

        {/* ROW 1 — Summary header card */}
        <div
          className="rounded-xl border bg-white p-5"
          style={{ borderColor: BORDER }}
        >
          {editingMeta ? (
            <input
              className="w-full text-[20px] font-bold outline-none border-b pb-1"
              style={{ borderColor: BORDER }}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
            />
          ) : (
            <h2 className="text-[20px] font-bold leading-tight">
              {decision.title}
            </h2>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
            {decision.code && <ReferenceIdBadge id={decision.code} />}
            {editingMeta ? (
              <>
                <select
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  {statuses.length === 0 ? (
                    <>
                      <option value="open">Open</option>
                      <option value="decided">Decided</option>
                      <option value="implemented">Implemented</option>
                      <option value="archived">Archived</option>
                    </>
                  ) : (
                    statuses.map((s) => (
                      <option key={s.id} value={s.value}>
                        {s.label}
                      </option>
                    ))
                  )}
                </select>
                <select
                  value={categoryDraft}
                  onChange={(e) => setCategoryDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  <option value="">No category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <select
                  value={impactDraft}
                  onChange={(e) => setImpactDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  {impacts.length === 0 ? (
                    <>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </>
                  ) : (
                    impacts.map((i) => (
                      <option key={i.id} value={i.value}>
                        {i.label}
                      </option>
                    ))
                  )}
                </select>
                <div
                  className="inline-flex rounded-md border p-0.5"
                  style={{ borderColor: BORDER }}
                >
                  {(["team", "private"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVisibilityDraft(v)}
                      className="px-2 py-0.5 text-[11px] font-medium rounded"
                      style={{
                        background:
                          visibilityDraft === v
                            ? "hsl(210,55%,88%)"
                            : "transparent",
                        color:
                          visibilityDraft === v
                            ? "hsl(210,100%,25%)"
                            : "hsl(215,16%,47%)",
                      }}
                    >
                      {v === "team" ? "Team" : "Private"}
                    </button>
                  ))}
                </div>
                <Input
                  type="date"
                  value={decidedAtDraft}
                  onChange={(e) => setDecidedAtDraft(e.target.value)}
                  className="h-7 w-[150px] text-[12px]"
                />
                <Input
                  type="date"
                  value={reviewDateDraft}
                  onChange={(e) => setReviewDateDraft(e.target.value)}
                  className="h-7 w-[150px] text-[12px]"
                />
              </>
            ) : (
              <>
                <StatusBadge value={decision.status} label={statusLabel} />
                {categoryLabel && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-[hsl(210,30%,94%)] text-[hsl(215,20%,38%)]">
                    {categoryLabel}
                  </span>
                )}
                <ImpactBadge value={decision.impact} label={impactLabel} />
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{
                    background:
                      decision.visibility === "private"
                        ? "hsl(215,15%,94%)"
                        : "hsl(210,85%,95%)",
                    color:
                      decision.visibility === "private"
                        ? "hsl(215,20%,38%)"
                        : "hsl(210,85%,38%)",
                  }}
                >
                  {decision.visibility === "private" ? "Private" : "Team"}
                </span>
                {decision.isPinned && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
                    style={{
                      background: "hsl(40,95%,94%)",
                      color: "hsl(35,85%,38%)",
                      borderColor: "hsl(40,85%,80%)",
                    }}
                  >
                    <Pin
                      className="h-3 w-3"
                      strokeWidth={1.5}
                      style={{ fill: "hsl(40,95%,55%)" }}
                    />
                    Pinned
                  </span>
                )}
              </>
            )}
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
              {decision.decidedByName ??
                decision.submittedByName ??
                "—"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
              {decision.decidedAt
                ? formatDate(decision.decidedAt)
                : formatDate(decision.createdAt)}
            </span>
          </div>

          {/* Context breadcrumb */}
          <div className="mt-3 flex flex-wrap items-center gap-1 text-[12px] text-muted-foreground">
            {decision.companyName && (
              <>
                <Link
                  to={`/companies/${decision.companyId}`}
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {decision.companyName}
                </Link>
                {decision.productName && (
                  <ChevronRight
                    className="h-3 w-3 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                )}
              </>
            )}
            {decision.productName && (
              <>
                <Link
                  to={`/products/${decision.productId}`}
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  <Package className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {decision.productName}
                </Link>
                {decision.projectName && (
                  <ChevronRight
                    className="h-3 w-3 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                )}
              </>
            )}
            {decision.projectName && (
              <Link
                to={`/projects/${decision.projectId}`}
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                <FolderKanban className="h-3.5 w-3.5" strokeWidth={1.5} />
                {decision.projectName}
              </Link>
            )}
            {decision.phaseName && (
              <>
                <ChevronRight
                  className="h-3 w-3 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <span>{decision.phaseName}</span>
              </>
            )}
            {decision.stageName && (
              <>
                <ChevronRight
                  className="h-3 w-3 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <span>{decision.stageName}</span>
              </>
            )}
            {decision.meetingTitle && (
              <>
                <span className="px-1.5">|</span>
                <Link
                  to={`/meetings/${decision.meetingId}`}
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {decision.meetingTitle}
                </Link>
              </>
            )}
          </div>

          {/* Linked Context — always visible read-mode summary of FKs */}
          <div
            className="mt-3 rounded-lg border bg-[hsl(210,30%,98%)] p-3"
            style={{ borderColor: "hsl(210,30%,88%)" }}
          >
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Linked Context
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 text-[12px]">
              <LinkedRow
                label="Company"
                value={decision.companyName}
                href={
                  decision.companyId
                    ? `/companies/${decision.companyId}`
                    : null
                }
              />
              <LinkedRow
                label="Product"
                value={decision.productName}
                href={
                  decision.productId
                    ? `/products/${decision.productId}`
                    : null
                }
              />
              <LinkedRow
                label="Project"
                value={decision.projectName}
                href={
                  decision.projectId
                    ? `/projects/${decision.projectId}`
                    : null
                }
              />
              <LinkedRow
                label="Meeting"
                value={
                  decision.meetingTitle
                    ? `${decision.meetingCode ?? ""} ${decision.meetingTitle}`.trim()
                    : null
                }
                href={
                  decision.meetingId
                    ? `/meetings/${decision.meetingId}`
                    : null
                }
              />
            </div>
            <div
              className="mt-2 flex gap-2 border-t pt-2 text-[12px]"
              style={{ borderColor: "hsl(210,30%,90%)" }}
            >
              <span className="w-20 shrink-0 pt-0.5 text-muted-foreground">
                Ideas:
              </span>
              {linkedIdeas.length === 0 ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  {linkedIdeas.map((idea) => (
                    <Link
                      key={idea.id}
                      to={`/ideas/${idea.id}`}
                      className="inline-flex items-center gap-1.5 text-foreground hover:underline"
                    >
                      <Lightbulb
                        className="h-3.5 w-3.5 shrink-0 text-[hsl(35,85%,45%)]"
                        strokeWidth={1.5}
                      />
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {idea.code ?? "—"}
                      </span>
                      <span className="truncate">· {idea.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {editingMeta ? (
              tagOptions.map((opt) => {
                const selected = tagsDraft.includes(opt.value);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() =>
                      setTagsDraft((prev) =>
                        prev.includes(opt.value)
                          ? prev.filter((v) => v !== opt.value)
                          : [...prev, opt.value],
                      )
                    }
                    style={{ opacity: selected ? 1 : 0.5 }}
                  >
                    <TagBadge
                      value={opt.value}
                      label={opt.label}
                      size="md"
                    />
                  </button>
                );
              })
            ) : decision.tags.length === 0 ? (
              <span className="text-[11px] text-muted-foreground italic">
                No tags
              </span>
            ) : (
              decision.tags.map((t) => {
                const opt = tagOptions.find((o) => o.value === t);
                return (
                  <TagBadge
                    key={t}
                    value={t}
                    label={opt?.label ?? t}
                    size="md"
                  />
                );
              })
            )}
          </div>

          {editingMeta && (
            <div className="mt-4 border-t pt-4" style={{ borderColor: BORDER }}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-[11px] uppercase text-muted-foreground">
                    Decided By
                  </label>
                  <select
                    value={decidedByIdDraft ?? ""}
                    onChange={(e) =>
                      setDecidedByIdDraft(e.target.value || null)
                    }
                    className="mt-1 flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="">—</option>
                    {allUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-end gap-2 text-sm pb-2">
                  <input
                    type="checkbox"
                    checked={isPinnedDraft}
                    onChange={(e) => setIsPinnedDraft(e.target.checked)}
                  />
                  Pinned
                </label>
              </div>
              <AssociationFields
                companyId={companyIdDraft}
                productId={productIdDraft}
                projectId={projectIdDraft}
                meetingId={meetingIdDraft}
                showMeeting
                onChange={(field, value) => {
                  if (field === "companyId") setCompanyIdDraft(value);
                  else if (field === "productId") setProductIdDraft(value);
                  else if (field === "projectId") setProjectIdDraft(value);
                  else if (field === "meetingId") setMeetingIdDraft(value);
                }}
              />
              <div className="mt-4">
                <label className="text-[11px] uppercase text-muted-foreground">
                  Linked Ideas
                </label>
                <div className="mt-1">
                  <IdeasMultiSelect
                    selectedIds={ideaIdsDraft}
                    onChange={setIdeaIdsDraft}
                    knownIdeas={decision.linkedIdeas ?? []}
                  />
                </div>
              </div>
            </div>
          )}

          {editingMeta && (
            <div className="mt-4 flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleMetaSave}
                disabled={updateDecision.isPending}
              >
                <Check className="h-4 w-4" strokeWidth={1.5} />
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditingMeta(false);
                  setTitleDraft(decision.title);
                  setStatusDraft(decision.status);
                  setCategoryDraft(decision.category ?? "");
                  setImpactDraft(decision.impact);
                  setVisibilityDraft(
                    (decision.visibility as "team" | "private") ?? "team",
                  );
                  setTagsDraft(decision.tags);
                  setDecidedAtDraft(
                    decision.decidedAt
                      ? decision.decidedAt.slice(0, 10)
                      : "",
                  );
                  setReviewDateDraft(
                    decision.reviewDate
                      ? decision.reviewDate.slice(0, 10)
                      : "",
                  );
                  setIsPinnedDraft(decision.isPinned);
                  setDecidedByIdDraft(decision.decidedById ?? null);
                  setCompanyIdDraft(decision.companyId ?? null);
                  setProductIdDraft(decision.productId ?? null);
                  setProjectIdDraft(decision.projectId ?? null);
                  setMeetingIdDraft(decision.meetingId ?? null);
                  setIdeaIdsDraft(
                    (decision.linkedIdeas ?? []).map((i) => i.id),
                  );
                }}
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
                Cancel
              </Button>
            </div>
          )}

          {/* VOTE BAR */}
          <div
            className="mt-4 flex flex-wrap items-center gap-3 border-t pt-4"
            style={{ borderColor: BORDER }}
          >
            <VoteButtons
              votesUp={decision.votesUp}
              votesDown={decision.votesDown}
              userVote={decision.currentUserVote}
              onVote={handleVote}
              size="md"
            />
            <span className="text-[12px] text-muted-foreground">
              {decision.votesUp} up · {decision.votesDown} down
            </span>
            {decision.currentUserVote && (
              <span className="text-[12px] font-medium text-foreground">
                Your vote:{" "}
                <span className="capitalize">
                  {decision.currentUserVote}
                </span>
              </span>
            )}
          </div>

          {/* Timeline for decided/implemented */}
          {showTimeline && (
            <div
              className="mt-4 flex items-center gap-3 border-t pt-4 text-[12px] text-muted-foreground"
              style={{ borderColor: BORDER }}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ background: "hsl(215,16%,65%)" }}
                />
                <span>Submitted: {formatDate(decision.createdAt)}</span>
              </div>
              <span className="text-muted-foreground">→</span>
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{
                    background: decision.decidedAt
                      ? "hsl(142,65%,45%)"
                      : "hsl(215,16%,65%)",
                  }}
                />
                <span>
                  Decided:{" "}
                  {decision.decidedAt
                    ? formatDate(decision.decidedAt)
                    : "—"}
                </span>
              </div>
              {decision.status === "implemented" && (
                <>
                  <span className="text-muted-foreground">→</span>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{
                        background: decision.implementedAt
                          ? "hsl(142,65%,45%)"
                          : "hsl(215,16%,65%)",
                      }}
                    />
                    <span>
                      Implemented:{" "}
                      {decision.implementedAt
                        ? formatDate(decision.implementedAt)
                        : "—"}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ROW 2 — Content */}
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: headings.length > 0 ? "1fr 200px" : "1fr",
          }}
        >
          <div
            className="overflow-hidden rounded-xl border bg-white"
            style={{ borderColor: BORDER }}
          >
            <div
              className="flex items-center justify-between border-b px-5 py-3"
              style={{ borderColor: BORDER }}
            >
              <span className="text-[13px] font-semibold">Content</span>
              <div className="flex items-center gap-3">
                {editingContent && (
                  <>
                    <span className="text-[11px] text-muted-foreground">
                      {wordCount} words
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {saveState === "saving"
                        ? "Saving..."
                        : saveState === "saved"
                          ? "Saved ✓"
                          : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => void doContentSave()}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-[hsl(142,65%,30%)] hover:bg-[hsl(142,55%,94%)]"
                      title="Save"
                    >
                      <Check className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setContentDraft(decision.content);
                        initialContent.current = decision.content;
                        setEditingContent(false);
                        setSaveState("idle");
                      }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-destructive hover:bg-[hsl(0,70%,96%)]"
                      title="Cancel"
                    >
                      <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </>
                )}
                {!editingContent && (
                  <button
                    type="button"
                    onClick={() => setEditingContent(true)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 py-5">
              {editingContent ? (
                <RichTextEditor
                  content={contentDraft}
                  onChange={setContentDraft}
                  toolbarVariant="full"
                  minHeight={400}
                  placeholder="Describe the decision..."
                />
              ) : decision.content ? (
                <div
                  className="rich-text-content prose prose-sm max-w-none"
                  style={{ fontSize: 14, lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: decision.content }}
                />
              ) : (
                <div className="py-10 text-center text-sm text-muted-foreground italic">
                  No content yet. Click Edit to start writing.
                </div>
              )}
            </div>
          </div>

          {headings.length > 0 && (
            <div
              className="h-fit rounded-xl border bg-white p-4 sticky"
              style={{ borderColor: BORDER, top: 80 }}
            >
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                On this page
              </div>
              <ul className="space-y-1 text-[12px]">
                {headings.map((h) => (
                  <li
                    key={h.id}
                    style={{ paddingLeft: (h.level - 1) * 10 }}
                    className={
                      h.level === 1
                        ? "font-semibold text-foreground"
                        : h.level === 2
                          ? "text-foreground"
                          : "text-muted-foreground"
                    }
                  >
                    <span className="cursor-default truncate block">
                      {h.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ROW 3 — Tabs */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <div
            className="flex items-center gap-1 border-b px-3 pt-2"
            style={{ borderColor: BORDER }}
          >
            {(
              [
                {
                  key: "linkedIdeas",
                  label: "Linked Ideas",
                  count: linkedIdeas.length,
                },
                { key: "tasks", label: "Tasks", count: decision.tasksCount ?? 0 },
                {
                  key: "actions",
                  label: "Actions",
                  count: decision.actionsCount ?? 0,
                },
                {
                  key: "comments",
                  label: "Comments",
                  count: comments.length,
                },
                {
                  key: "attachments",
                  label: "Attachments",
                  count: decision.attachmentsCount ?? 0,
                },
              ] as const
            ).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium transition-colors"
                style={{
                  borderBottom:
                    tab === t.key
                      ? "2px solid hsl(210,100%,50%)"
                      : "2px solid transparent",
                  color:
                    tab === t.key
                      ? "hsl(210,100%,28%)"
                      : "hsl(215,16%,47%)",
                }}
              >
                {t.label}
                <span
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
                  style={{
                    background: "hsl(210,30%,94%)",
                    color: "hsl(210,30%,40%)",
                  }}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          <div className="p-5">
            {tab === "linkedIdeas" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-muted-foreground">
                    {linkedIdeas.length} linked
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setLinkPickerOpen(true)}
                  >
                    <Link2 className="h-4 w-4" strokeWidth={1.5} />
                    Link Idea
                  </Button>
                </div>

                {linkedIdeas.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No ideas linked yet.
                  </div>
                ) : (
                  <div
                    className="overflow-hidden rounded-lg border"
                    style={{ borderColor: BORDER }}
                  >
                    <table
                      className="w-full text-[13px]"
                      style={{ borderCollapse: "collapse" }}
                    >
                      <thead className="bg-[hsl(210,30%,97%)] text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2" style={{ width: 100 }}>
                            #
                          </th>
                          <th className="px-3 py-2">Title</th>
                          <th className="px-3 py-2" style={{ width: 100 }}>
                            Status
                          </th>
                          <th className="px-3 py-2" style={{ width: 90 }}>
                            Priority
                          </th>
                          <th className="px-3 py-2" style={{ width: 100 }}>
                            Votes
                          </th>
                          <th className="px-3 py-2" style={{ width: 140 }}>
                            Submitted By
                          </th>
                          <th
                            className="px-3 py-2 text-right"
                            style={{ width: 90 }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {linkedIdeas.map((idea) => (
                          <tr key={idea.id}>
                            <td className="px-3 py-2 font-mono text-[12px] text-muted-foreground">
                              {idea.code ?? "—"}
                            </td>
                            <td className="px-3 py-2">
                              <Link
                                to={`/ideas/${idea.id}`}
                                className="hover:underline"
                              >
                                {idea.title}
                              </Link>
                            </td>
                            <td className="px-3 py-2 capitalize text-muted-foreground">
                              {idea.status}
                            </td>
                            <td className="px-3 py-2 capitalize text-muted-foreground">
                              {idea.priority}
                            </td>
                            <td className="px-3 py-2 text-[12px] text-muted-foreground">
                              {idea.votesUp} / {idea.votesDown}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {idea.submittedByName ?? "—"}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => handleUnlinkIdea(idea.id)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]"
                                title="Unlink"
                              >
                                <Trash2
                                  className="h-[14px] w-[14px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {linkPickerOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[9990] bg-black/30"
                      onClick={() => setLinkPickerOpen(false)}
                    />
                    <div
                      className="fixed left-1/2 top-1/2 z-[9991] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-white p-5 shadow-2xl"
                      style={{ borderColor: BORDER }}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Lightbulb
                            className="h-4 w-4 text-foreground"
                            strokeWidth={1.5}
                          />
                          <span className="text-[14px] font-semibold">
                            Link Idea
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setLinkPickerOpen(false)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </div>
                      <Input
                        autoFocus
                        placeholder="Search ideas..."
                        value={linkSearch}
                        onChange={(e) => setLinkSearch(e.target.value)}
                      />
                      <div className="mt-3 max-h-[340px] space-y-1 overflow-y-auto">
                        {ideasForLink.length === 0 ? (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No ideas found.
                          </div>
                        ) : (
                          ideasForLink.map((idea) => {
                            const alreadyLinked = linkedIdeas.some(
                              (li) => li.id === idea.id,
                            );
                            return (
                              <button
                                key={idea.id}
                                type="button"
                                disabled={alreadyLinked}
                                onClick={() => void handleLinkIdea(idea.id)}
                                className="flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left hover:bg-[hsl(210,50%,98%)] disabled:opacity-50"
                                style={{ borderColor: BORDER }}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                                    <span className="font-mono">
                                      {idea.code ?? "—"}
                                    </span>
                                    <span className="capitalize">
                                      {idea.status}
                                    </span>
                                  </div>
                                  <div className="truncate text-[13px] font-medium">
                                    {idea.title}
                                  </div>
                                </div>
                                {alreadyLinked && (
                                  <CheckCircle2
                                    className="h-4 w-4 text-[hsl(142,65%,45%)]"
                                    strokeWidth={1.5}
                                  />
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "tasks" && (
              <TasksTabContent
                decisionId={decision.id}
                prefill={{
                  companyId: decision.companyId,
                  productId: decision.productId,
                  projectId: decision.projectId,
                  decisionId: decision.id,
                  decisionTitle: decision.title,
                  locked: { decisionId: true },
                }}
              />
            )}

            {tab === "actions" && (
              <ActionsTabContent
                decisionId={decision.id}
                prefill={{
                  companyId: decision.companyId,
                  productId: decision.productId,
                  projectId: decision.projectId,
                  decisionId: decision.id,
                  decisionTitle: decision.title,
                  locked: { decisionId: true },
                }}
              />
            )}

            {tab === "comments" && (
              <div className="space-y-4">
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {comments.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No comments yet. Start the discussion.
                    </div>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="flex gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,88%)] text-[11px] font-semibold text-[hsl(210,100%,25%)]">
                          {(c.authorName ?? "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-[12px]">
                            <span className="font-medium">
                              {c.authorName ?? "Unknown"}
                            </span>
                            <span className="text-muted-foreground">
                              {formatRelative(c.createdAt)}
                            </span>
                            {user?.id === c.authorId && (
                              <button
                                type="button"
                                onClick={() =>
                                  void deleteComment.mutateAsync(c.id)
                                }
                                className="ml-auto text-muted-foreground hover:text-destructive"
                                title="Delete"
                              >
                                <Trash2
                                  className="h-3.5 w-3.5"
                                  strokeWidth={1.5}
                                />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-[13px]">
                            {c.content}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div
                  className="flex gap-2 border-t pt-4"
                  style={{ borderColor: BORDER }}
                >
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        void handleAddComment();
                      }
                    }}
                    placeholder="Write a comment... (Ctrl+Enter to submit)"
                    className="flex-1 resize-none rounded-md border px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-ring"
                    style={{ borderColor: BORDER, minHeight: 60 }}
                  />
                  <Button
                    onClick={() => void handleAddComment()}
                    disabled={!commentText.trim() || addComment.isPending}
                  >
                    <Send className="h-4 w-4" strokeWidth={1.5} />
                    Send
                  </Button>
                </div>
              </div>
            )}

            {tab === "attachments" && (
              <FileUploadZone
                entityType="decision"
                entityId={decision.id}
                entityLabel={decision.code}
                companyId={decision.companyId ?? undefined}
                productId={decision.productId ?? undefined}
                projectId={decision.projectId ?? undefined}
                meetingId={decision.meetingId ?? undefined}
                showExisting
              />
            )}
          </div>
        </div>

        {/* ROW 4 — Metadata */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <button
            type="button"
            onClick={() => setMetaExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-3 text-left"
          >
            <span className="text-[13px] font-semibold">Decision Details</span>
            {metaExpanded ? (
              <ChevronDown
                className="h-4 w-4 text-muted-foreground"
                strokeWidth={1.5}
              />
            ) : (
              <ChevronRight
                className="h-4 w-4 text-muted-foreground"
                strokeWidth={1.5}
              />
            )}
          </button>
          {metaExpanded && (
            <div
              className="space-y-4 border-t p-5"
              style={{ borderColor: BORDER }}
            >
              <div className="grid grid-cols-4 gap-4 text-[13px]">
                <Field
                  label="Submitted By"
                  value={decision.submittedByName ?? "—"}
                />
                <Field
                  label="Submitted At"
                  value={new Date(decision.createdAt).toLocaleString()}
                />
                <Field
                  label="Decided By"
                  value={decision.decidedByName ?? "—"}
                />
                <Field
                  label="Decided At"
                  value={formatDate(decision.decidedAt)}
                />
                <Field
                  label="Implemented At"
                  value={formatDate(decision.implementedAt)}
                />
                <Field
                  label="Review Date"
                  value={formatDate(decision.reviewDate)}
                />
                <Field label="Status" value={statusLabel} />
                <Field label="Impact" value={impactLabel} />
              </div>
              <div
                className="border-t pt-4"
                style={{ borderColor: BORDER }}
              >
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Linked Context
                </div>
                <div className="grid grid-cols-2 gap-y-2 text-[13px]">
                  <LinkedRow
                    label="Company"
                    value={decision.companyName}
                    href={
                      decision.companyId
                        ? `/companies/${decision.companyId}`
                        : null
                    }
                  />
                  <LinkedRow
                    label="Product"
                    value={decision.productName}
                    href={
                      decision.productId
                        ? `/products/${decision.productId}`
                        : null
                    }
                  />
                  <LinkedRow
                    label="Project"
                    value={decision.projectName}
                    href={
                      decision.projectId
                        ? `/projects/${decision.projectId}`
                        : null
                    }
                  />
                  <LinkedRow label="Phase" value={decision.phaseName} />
                  <LinkedRow label="Stage" value={decision.stageName} />
                  <LinkedRow
                    label="Meeting"
                    value={
                      decision.meetingTitle
                        ? `${decision.meetingCode ?? ""} ${decision.meetingTitle}`.trim()
                        : null
                    }
                    href={
                      decision.meetingId
                        ? `/meetings/${decision.meetingId}`
                        : null
                    }
                  />
                  <LinkedRow label="Category" value={categoryLabel} />
                  <LinkedRow
                    label="Visibility"
                    value={
                      decision.visibility === "private" ? "Private" : "Team"
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete decision?"
        description={`Are you sure you want to delete '${decision.title}'? It will be removed from your Decisions list.`}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={unlinkIdeaId !== null}
        onOpenChange={(open) => {
          if (!open) setUnlinkIdeaId(null);
        }}
        title="Unlink idea?"
        description="Are you sure you want to unlink this idea from this decision?"
        confirmLabel="Unlink"
        onConfirm={confirmUnlinkIdea}
      />
    </AppLayout>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-[13px] text-foreground">{value}</div>
    </div>
  );
}

function LinkedRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string | null;
  href?: string | null;
}) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 text-muted-foreground">{label}:</span>
      {value ? (
        href ? (
          <Link to={href} className="text-foreground hover:underline">
            {value}
          </Link>
        ) : (
          <span className="text-foreground">{value}</span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}
